import type { TourSection } from './types';

// Helper: clickea el botón del tab si no está activo y espera 2 frames
// para que React monte el panel del tab antes de buscar el target.
function openClientTab(key: string): () => Promise<void> {
  return async () => {
    const btn = document.querySelector<HTMLButtonElement>(
      `[data-tour="client-tab-${key}"]`,
    );
    if (btn && btn.getAttribute('aria-selected') !== 'true') {
      btn.click();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    }
  };
}

export const adminClientsDetailSections: TourSection[] = [
  {
    id: 'tab-general',
    label: 'Tab General',
    steps: [
      {
        target: '[data-tour="client-detail-general"]',
        title: 'Datos generales',
        content:
          'Nombre, identificador único (slug), tipo (interno o externo), y si está activo o no. Si está inactivo, el cliente no puede usar su llave de acceso aunque la tenga.',
        before: openClientTab('general'),
      },
    ],
  },
  {
    id: 'tab-permissions',
    label: 'Tab Permisos',
    steps: [
      {
        target: '[data-tour="client-detail-permissions"]',
        title: 'Qué puede hacer el cliente',
        content:
          'Defines si el cliente puede leer productos, modificarlos, y qué campos específicos. También si puede usar el endpoint avanzado de consultas (lectura con prioridad de fuentes).',
        before: openClientTab('permissions'),
      },
    ],
  },
  {
    id: 'tab-webhooks',
    label: 'Tab Webhooks',
    steps: [
      {
        target: '[data-tour="client-detail-webhooks"]',
        title: 'Notificaciones al cliente',
        content:
          'Configura una URL del cliente donde el sistema le avisa automáticamente cada vez que un producto cambia. Incluye una clave secreta para que el cliente verifique que el aviso viene de nosotros (no de un tercero). Con "Avisar cambios de inventario" también le llega un aviso por cada cambio de stock, si sus campos incluyen la cantidad. Filtros opcionales: solo notificar productos activos, etc.',
        before: openClientTab('webhooks'),
      },
    ],
  },
  {
    id: 'tab-source-scopes',
    label: 'Tab Fuentes de datos',
    steps: [
      {
        target: '[data-tour="client-detail-source-scopes"]',
        title: 'Fuentes habilitadas',
        content:
          'Lista de orígenes de información del catálogo (ej: master, sap, axioma, vtex) que este cliente puede leer. Si tiene varias, puedes definir el orden de prioridad en la pestaña siguiente.',
        before: openClientTab('source-scopes'),
      },
    ],
  },
  {
    id: 'tab-api-key',
    label: 'Tab API Key',
    steps: [
      {
        target: '[data-tour="client-detail-api-key"]',
        title: 'Clave de acceso',
        content:
          'La clave se guarda enmascarada por seguridad — solo ves los últimos caracteres. El botón "Rotar" genera una clave nueva y muestra el valor completo UNA SOLA VEZ; cópiala inmediatamente. La clave vieja queda invalidada.',
        before: openClientTab('api-key'),
      },
    ],
  },
  {
    id: 'tab-field-priority',
    label: 'Tab Prioridad de campos',
    steps: [
      {
        target: '[data-tour="client-detail-field-priority"]',
        title: 'Orden de fuentes para cada campo',
        content:
          'Configura de qué fuente toma cada campo cuando este cliente lee un producto. Por ejemplo: para el nombre priorizar la fuente "vtex", para la imagen priorizar "master". La primera fuente con dato no vacío gana.',
        before: openClientTab('field-priority'),
      },
    ],
  },
  {
    id: 'tab-scope',
    label: 'Tab Alcance',
    steps: [
      {
        target: '[data-tour="client-detail-scope"]',
        title: 'Qué parte del catálogo recibe',
        content:
          'Elige las marcas y categorías que este cliente puede ver. Al elegir una categoría se incluyen también todas las que cuelgan de ella, y abajo se listan cuáles son. Si la lista es larga, «ver solo elegidas» esconde lo que no está marcado. El alcance vale para todo lo que el cliente recibe: las consultas de productos, los listados de marcas, categorías y modelos, los envíos automáticos y lo que el cliente puede crear o editar. Un producto sin marca o sin categoría no entra en ningún alcance. Y ojo con dejarlo vacío: en un cliente externo eso significa que no recibe nada, y en uno interno que recibe todo.',
        before: openClientTab('scope'),
      },
    ],
  },
  {
    id: 'tab-audit',
    label: 'Tab Auditoría',
    steps: [
      {
        target: '[data-tour="client-detail-audit"]',
        title: 'Historial del cliente',
        content:
          'Quién creó, editó o eliminó cosas de este cliente, y cuándo. Sirve para revisar cambios pasados.',
        before: openClientTab('audit'),
      },
    ],
  },
];
